import { v4 as uuidv4 } from 'uuid';
import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import { updatePublicAPI } from '@/service/publicService';
import { createTmpUploadedImgs } from '../../../handler/ChatAIHandler';
import { validateAttachmentBatch } from './AiChatAttachmentHandler';

/**
 * Create temporary image objects from files
 * @param {File[]} files - List of files
 * @returns {Object[]} - List of image objects with id, localUrl, fileData, uploadStatus
 */
const createTmpImageObjects = (files) => {
    return files.map(file => ({
        id: `tmp_attach_${uuidv4()}`,
        localUrl: URL.createObjectURL(file),
        fileData: file,
        uploadStatus: Constant.AI_CHAT_UPLOAD_STATUS.UPLOADING
    }));
};

/**
 * Remove uploaded image
 * @param {Object} removeImg - Image to remove
 * @param {Function} setUploadImgs - Set uploaded images function
 */
const removeUploadedImage = (removeImg, setUploadImgs) => {
    setUploadImgs(preImg => preImg.filter(img => img.id !== removeImg.id));
    removeImg.localUrl && URL.revokeObjectURL(removeImg.localUrl);
    // Failed uploads have no S3 object to delete
    if (removeImg.s3Path) {
        updatePublicAPI(null, {
            updateApiType: Constant.UPDATE_API_PUBLIC_TYPE.DELETE_S3_FILE_BY_PATH,
            s3Path: removeImg.s3Path
        });
    }
};

/**
 * Cleanup uploaded images (revoke object URLs)
 * @param {Object[]} uploadImgs - List of uploaded images
 * @param {Function} setUploadImgs - Set uploaded images function
 */
const cleanupUploadedImages = (uploadImgs, setUploadImgs) => {
    if (Utils.isEmptyArray(uploadImgs)) return;
    for (let img of uploadImgs) {
        img.localUrl && URL.revokeObjectURL(img.localUrl);
    }
    setUploadImgs([]);
};

/**
 * Check if device supports image capture (mobile camera)
 * @returns {boolean} - true if supports, false otherwise
 */
const checkImageCaptureSupport = () => {
    const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const testInput = document.createElement('input');
    testInput.type = 'file';
    const supportsCapture = 'capture' in testInput;
    return isMobile && supportsCapture;
};

const handleUploadCapturedImg = ({
    file,
    currentUploadImgs = [],
    currentUploadFiles = [],
    setUploadImgs,
    enqueueSnackbar,
    corporationDetail,
    projectInuseDetail,
    chatThreadId
}) => {
    if (!file) return;

    const isValid = validateAttachmentBatch({
        newFiles: [file],
        uploadImgs: currentUploadImgs,
        uploadFiles: currentUploadFiles,
        enqueueSnackbar
    });
    if (!isValid) return;

    const imgs = createTmpImageObjects([file]);
    setUploadImgs(prev => [...prev, ...imgs]);
    createTmpUploadedImgs(corporationDetail.id, projectInuseDetail.id, chatThreadId, imgs, setUploadImgs);
};

export {
    createTmpImageObjects,
    removeUploadedImage,
    cleanupUploadedImages,
    checkImageCaptureSupport,
    handleUploadCapturedImg
};
